// Generate robust browser fingerprint for tracking anonymous attempts
export const generateFingerprint = async () => {
  const components = [];

  // Basic browser info
  components.push(navigator.userAgent);
  components.push(navigator.language);
  components.push(navigator.languages?.join(',') || '');
  components.push(screen.width + 'x' + screen.height);
  components.push(screen.availWidth + 'x' + screen.availHeight);
  components.push(screen.colorDepth);
  components.push(screen.pixelDepth);
  components.push(new Date().getTimezoneOffset());
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  components.push(navigator.hardwareConcurrency || 0);
  components.push(navigator.deviceMemory || 0);
  components.push(navigator.platform);
  components.push(navigator.maxTouchPoints || 0);
  components.push(navigator.cookieEnabled);
  components.push(typeof navigator.doNotTrack);

  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 100, 50);
    ctx.fillStyle = '#069';
    ctx.fillText('Buildex.dev 🎨', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Buildex.dev 🎨', 4, 17);
    components.push(canvas.toDataURL());
  } catch (e) {
    components.push('canvas-error');
  }

  // WebGL fingerprint
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
      components.push(gl.getParameter(gl.VERSION));
      components.push(gl.getParameter(gl.SHADING_LANGUAGE_VERSION));
    }
  } catch (e) {
    components.push('webgl-error');
  }

  // Audio fingerprint (using AudioContext properties only - no deprecated APIs)
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    components.push(audioContext.sampleRate);
    components.push(audioContext.destination.maxChannelCount);
    components.push(audioContext.destination.numberOfInputs);
    components.push(audioContext.destination.numberOfOutputs);
    audioContext.close();
  } catch (e) {
    components.push('audio-error');
  }

  // Installed plugins (legacy but still useful)
  try {
    const plugins = [];
    for (let i = 0; i < navigator.plugins.length && i < 10; i++) {
      plugins.push(navigator.plugins[i].name);
    }
    components.push(plugins.join(','));
  } catch (e) {
    components.push('plugins-error');
  }

  // Font detection (check for common fonts)
  try {
    const testFonts = ['Arial', 'Helvetica', 'Times', 'Courier', 'Verdana', 'Georgia', 'Comic Sans MS', 'Impact'];
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const getWidth = (font) => {
      ctx.font = `${testSize} ${font}`;
      return ctx.measureText(testString).width;
    };

    const baseWidths = baseFonts.map(getWidth);
    const detectedFonts = testFonts.filter(font => {
      return baseFonts.some((baseFont, i) => {
        ctx.font = `${testSize} "${font}", ${baseFont}`;
        return ctx.measureText(testString).width !== baseWidths[i];
      });
    });
    components.push(detectedFonts.join(','));
  } catch (e) {
    components.push('fonts-error');
  }

  // Create hash using SHA-256-like algorithm
  const str = components.join('|||');
  let hash1 = 0, hash2 = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash1 = ((hash1 << 5) - hash1 + char) | 0;
    hash2 = ((hash2 << 7) - hash2 + char) | 0;
  }

  // Combine hashes for longer fingerprint
  const fp = Math.abs(hash1).toString(16).padStart(8, '0') +
             Math.abs(hash2).toString(16).padStart(8, '0') +
             Math.abs(hash1 ^ hash2).toString(16).padStart(8, '0');

  return fp;
};

export default generateFingerprint;
